/// <reference types="react" />
/// <reference types="react-dom" />

import * as React from 'react';
import ReactDOM from 'react-dom';
// Import createRoot from the correct location for React 18
// @ts-ignore - Type definition for react-dom/client might be missing
import { createRoot } from 'react-dom/client';
//import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { AuthConfig } from "./AuthConfig";
import { MsalAuthenticationTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';

// migration - Material UI components
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { appTheme } from './theme/appTheme';
import { Box, Button, Stack, Typography, useTheme, CircularProgress } from '@mui/material';

// Import Global Error Boundary
import { GlobalErrorBoundary } from './Components/ErrorBoundaries';
import logger from './Services/LoggingService';

const msalInstance = new PublicClientApplication(AuthConfig);

const UnauthenticatedContent: React.FC = () => {
  const theme = useTheme();
  
  return (
    <Stack
      spacing={6} // Equivalent to childrenGap: 50
      sx={{
        height: '100vh',
        width: '100%'
      }}
    >
      {/* Header section - equivalent to first Stack.Item */}
      <Box
        sx={{
          flex: 4, // Equivalent to grow={4}
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.semantic.warningDark, // Using theme token instead of hardcoded #ffb900
          color: 'white', // Equivalent to DefaultPalette.white
          textAlign: 'center',
          minHeight: '200px'
        }}
      >
        <Typography variant="h5" component="h4" fontWeight={500}>
          You need to be signed in to access this content.
        </Typography>
      </Box>
      
      {/* Login section - equivalent to second Stack.Item */}
      <Box
        sx={{
          flex: 3, // Equivalent to grow={3}
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white'
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => { document.location.reload() }}
          sx={{
            fontWeight: 500,
            padding: '12px 24px',
            fontSize: '1rem',
            textTransform: 'none', // Preserve original button text casing
            boxShadow: 2
          }}
        >
          Log in
        </Button>
      </Box>
    </Stack>
  );
};

// Loading component for MSAL initialization
const LoadingContent: React.FC = () => {
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body1" color="text.secondary">
        Initializing authentication...
      </Typography>
    </Box>
  );
};

// Get the root element
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Create React root once
let root: any;
try {
  // Using React 18's createRoot API
  root = createRoot(rootElement);
} catch (e) {
  // Fall back to legacy React 17 API - no root needed
  root = null;
}

// Render function that reuses the same root
const renderApp = (content: React.ReactElement) => {
  if (root) {
    // React 18
    root.render(content);
  } else {
    // React 17 fallback
    // @ts-ignore - Ignoring React 17 warning
    ReactDOM.render(content, rootElement);
  }
};

// Initialize MSAL and render the application
const initializeApp = async () => {
  try {
    // Initialize MSAL instance first
    await msalInstance.initialize();
    
    // Handle redirect promise after initialization
    await msalInstance.handleRedirectPromise();
    
    // Prepare the application content with Material UI components and Global Error Boundary
    const appContent = (
      <GlobalErrorBoundary>
        <MuiThemeProvider theme={appTheme}>
          <MsalProvider instance={msalInstance}>
            <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
              <App />
            </MsalAuthenticationTemplate>
            <UnauthenticatedTemplate>
              <UnauthenticatedContent />
            </UnauthenticatedTemplate>
          </MsalProvider>
        </MuiThemeProvider>
      </GlobalErrorBoundary>
    );

    // Render the application using the existing root
    renderApp(appContent);
  } catch (error) {
    logger.error("MSAL initialization failed", error as Error, null, {
      component: 'index',
      method: 'initializeApp'
    });
    
    // Render error content using the existing root
    const errorContent = (
      <GlobalErrorBoundary>
        <MuiThemeProvider theme={appTheme}>
          <Box
            sx={{
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              padding: 3,
              textAlign: 'center'
            }}
          >
            <Typography variant="h5" color="error" gutterBottom>
              Authentication Error
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              There was an error initializing the authentication system.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </Box>
        </MuiThemeProvider>
      </GlobalErrorBoundary>
    );

    renderApp(errorContent);
  }
};

// Show loading screen while initializing (also wrapped in GlobalErrorBoundary)
const loadingContent = (
  <GlobalErrorBoundary>
    <MuiThemeProvider theme={appTheme}>
      <LoadingContent />
    </MuiThemeProvider>
  </GlobalErrorBoundary>
);

// Render loading screen first
renderApp(loadingContent);

// Initialize the application
initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
